import { useEffect, useRef, useState } from "react";

// Project import
import { aiAssistantChatStream, aiAssistantChatThread } from "services/internalApi/aiChat";
import { generateUUID } from "utils/deviceTools";
import { AiChatMessage } from "types/auth";
import { mixpanelEvents } from "config/servicesConfig/mixpanelEvents";
import useAuth from "hooks/useAuth";
import useStateRef from "react-usestateref";
import { buildOnboardingPrompt, consumePendingWelcome } from "../utils/georgeOnboarding";
import useAppFunctionalityConfig from "hooks/useAppFunctionalityConfig";

export function useAIChat() {
  // --- Hooks --- //
  const { user, aiAssistant, setAIAssistantData, callCloudFunction, updateProfile, userOnboardingObject } = useAuth();
  const { getAppContextSnapshot } = useAppFunctionalityConfig();
  // --- State and refs --- //
  const [messages, setMessages, messagesRef] = useStateRef<AiChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingThread, setIsLoadingThread] = useState<boolean>(false);

  const abortControllerRef = useRef<AbortController | null>(null);
  const currentThreadIdRef = useRef<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const initializeChat = async () => {
      const latestThreadId =
        typeof user?.latestAiChatThreadId === "string" ? user.latestAiChatThreadId.trim() : "";

      const cachedThreadId =
        typeof aiAssistant.currentThreadId === "string" && aiAssistant.currentThreadId.trim()
          ? aiAssistant.currentThreadId.trim()
          : typeof currentThreadIdRef.current === "string" && currentThreadIdRef.current.trim()
          ? currentThreadIdRef.current.trim()
          : typeof user?.latestAiChatThreadId === "string" && user.latestAiChatThreadId.trim()
          ? user.latestAiChatThreadId.trim()
          : null;
      const cachedMessages = aiAssistant.currentMessages || [];

      if (latestThreadId && cachedThreadId === latestThreadId && cachedMessages.length > 0) {
        setMessages(cachedMessages);
        currentThreadIdRef.current = cachedThreadId;
        return;
      }

      if (!latestThreadId) {
        setMessages(cachedMessages);
        currentThreadIdRef.current = cachedThreadId ?? null;
        return;
      }

      setIsLoadingThread(true);

      try {
        const result = await aiAssistantChatThread({
          userId: user?.id || "local-test-user",
          threadId: latestThreadId,
        });
        
        if (!isMounted) return;

        const fetchedMessages: AiChatMessage[] = result.messages
          .filter((msg, index) => {
            // Remove onboarding message
            if (msg.content.length > 0) {
              const contentText = msg.content[0].text;
              if (contentText && contentText.startsWith("[ONBOARDING]")) {
                return false;
              }
            }
            // Only keep user/model messages
            return msg.role === "user" || msg.role === "model";
          })
          .map((msg) => ({
            id: msg.id ?? generateUUID(),
            userId: user?.id || "local-test-user",
            threadId: latestThreadId,
            role: msg.role === "model" ? "assistant" : "user",
            content:
              msg.content
                ?.filter((part) => typeof part?.text === "string")
                .map((part) => part.text)
                .join("") ?? "",
            createdAt: msg.savedAt ? new Date(msg.savedAt).getTime() : Date.now(),
            type: "normal",
          }));

        setMessages(fetchedMessages);
        currentThreadIdRef.current = latestThreadId;
        setAIAssistantData("messages", fetchedMessages);
        setAIAssistantData("threadId", latestThreadId);
      } catch (err) {
        if (!isMounted) return;
        setMessages(aiAssistant.currentMessages || []);
        currentThreadIdRef.current = aiAssistant.currentThreadId ?? null;
      } finally {
        if (isMounted) {
          setIsLoadingThread(false);
        }
      }
    };

    initializeChat();

    if (
      getAppContextSnapshot().app?.internalAppType === "george" &&
      user?.onboarded &&
      consumePendingWelcome()
    ) {
      triggerGeorgeOnboarding();
    }

    return () => {
      isMounted = false;
      if (messagesRef.current.length > 0) {
        setAIAssistantData("messages", messagesRef.current);
      }
      if (currentThreadIdRef.current) {
        setAIAssistantData("threadId", currentThreadIdRef.current);
      }
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
    };
  }, []);

  // Sync thread and messages from Redux if fetchAIConversation completes after mount
  useEffect(() => {
    if (currentThreadIdRef.current !== null) return;
    if (!aiAssistant.currentThreadId) return;
    currentThreadIdRef.current = aiAssistant.currentThreadId;
    setMessages(aiAssistant.currentMessages || []);
  }, [aiAssistant.currentThreadId]);

  const triggerGeorgeOnboarding = async () => {
    const prompt = buildOnboardingPrompt(userOnboardingObject?.questionaireAnswers);
    const threadId = getOrCreateThreadId();
    const assistantId = generateUUID();

    setMessages((prev) => [
      ...prev,
      {
        id: assistantId,
        userId: user?.id || "local-test-user",
        threadId,
        role: "assistant",
        content: "",
        createdAt: Date.now(),
        type: "normal",
      },
    ]);
    setIsLoading(true);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const finalReply = await aiAssistantChatStream(
        {
          userId: user?.id || "local-test-user",
          threadId,
          message: prompt,
          context: getAppContextSnapshot(),
        },
        {
          signal: controller.signal,
          onToken: (token) => {
            setMessages((prev) =>
              prev.map((msg) => msg.id === assistantId ? { ...msg, content: msg.content + token } : msg),
            );
          },
          onDone: (reply) => {
            setMessages((prev) =>
              prev.map((msg) => msg.id === assistantId ? { ...msg, content: reply } : msg),
            );
          },
          onError: () => {
            setMessages((prev) =>
              prev.map((msg) => msg.id === assistantId ? { ...msg, content: "ai-chat-error", type: "error" } : msg),
            );
          },
        },
      );
      setMessages((prev) =>
        prev.map((msg) => msg.id === assistantId ? { ...msg, content: finalReply } : msg),
      );
    } catch (err: any) {
      if (err?.name === "AbortError") return;
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantId ? { ...msg, content: "ai-chat-error", type: "error" } : msg,
        ),
      );
    } finally {
      if (abortControllerRef.current === controller) abortControllerRef.current = null;
      setIsLoading(false);
    }
  };

  const sendMessage = async (text: string) => {
    const now = Date.now();
    const threadId = getOrCreateThreadId();

    const userMessage: AiChatMessage = {
      id: generateUUID(),
      userId: user?.id || "local-test-user",
      threadId: threadId,
      role: "user",
      content: text,
      createdAt: now,
      type: "normal",
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    const assistantId = generateUUID();

    // Placeholder message (for streaming / typing)
    setMessages((prev) => [
      ...prev,
      {
        id: assistantId,
        userId: user?.id || "local-test-user",
        threadId: threadId,
        role: "assistant",
        content: "",
        createdAt: now,
        type: "normal",
      },
    ]);

    // Abort any existing stream before starting a new one
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      callCloudFunction("trackEvent", { event: mixpanelEvents.aiAssistantChatMessageSent });
      const finalReply = await aiAssistantChatStream(
        {
          userId: user?.id || "local-test-user",
          threadId: threadId,
          message: text,
          context: getAppContextSnapshot(),
        },
        {
          signal: controller.signal,
          onToken: (token: string) => {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantId ? { ...msg, content: msg.content + token } : msg,
              ),
            );
          },
          onDone: (reply: string) => {
            setMessages((prev) =>
              prev.map((msg) => (msg.id === assistantId ? { ...msg, content: reply } : msg)),
            );
          },
          onError: () => {
            callCloudFunction("trackEvent", { event: mixpanelEvents.aiAssistantChatMessageError });
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantId
                  ? {
                      ...msg,
                      content: "ai-chat-error",
                      type: "error",
                    }
                  : msg,
              ),
            );
          },
        },
      );
      callCloudFunction("trackEvent", { event: mixpanelEvents.aiAssistantChatMessageReceived });
      setMessages((prev) =>
        prev.map((msg) => (msg.id === assistantId ? { ...msg, content: finalReply } : msg)),
      );
    } catch (err: any) {
      if (err?.name === "AbortError") {
        return;
      }
      callCloudFunction("trackEvent", { event: mixpanelEvents.aiAssistantChatMessageError });
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantId
            ? {
                ...msg,
                content: "ai-chat-error",
                type: "error",
              }
            : msg,
        ),
      );
    } finally {
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }

    setAIAssistantData("messages", messagesRef.current);
    setAIAssistantData("threadId", currentThreadIdRef.current);

    setIsLoading(false);
  };

  const reset = () => {
    setAIAssistantData("threadId", null);
    setAIAssistantData("messages", []);
    currentThreadIdRef.current = null;
    void updateProfile(
      {
        id: user!.id,
        latestAiChatThreadId: null,
      },
      false,
    ).catch((err: unknown) => {
      console.error("Failed to update latest AI chat thread id", err);
    });
    setMessages([]);
    setIsLoading(false);
  };

  const getOrCreateThreadId = () => {
    if (currentThreadIdRef.current) return currentThreadIdRef.current;
    if (aiAssistant.currentThreadId) {
      currentThreadIdRef.current = aiAssistant.currentThreadId;
      return currentThreadIdRef.current;
    }
    const newThreadId = `thread-${Date.now()}`;
    currentThreadIdRef.current = newThreadId;
    setAIAssistantData("threadId", newThreadId);
    void updateProfile(
      {
        id: user!.id,
        latestAiChatThreadId: newThreadId,
      },
      false,
    ).catch((err: unknown) => {
      console.error("Failed to update latest AI chat thread id", err);
    });
    callCloudFunction("trackEvent", {
      event: mixpanelEvents.aiAssistantChatNewThreadStarted,
    });
    return newThreadId;
  };

  return {
    messages,
    isLoading,
    isLoadingThread,
    sendMessage,
    reset,
  };
}
